// Warning triangle with "!" for a pump whose kvar fault bit is set (backend
// `fault` / `fault_since` on /latest and /devices). It is a *shape*, additive
// to the device's status pulse and color -- never a recolor. Tint uses the
// existing severity tokens only: Problem Orange normally, critical red only
// when the device's own severity is critical. Shown even when alerts are
// muted: it's state, not a notification. The cause is unknowable remotely,
// so the copy makes no guess at one.

const TRIANGLE_PATH = 'M12 2.5 L22.5 20.5 H1.5 Z'
const BANG_PATH     = 'M12 9 V14.5'

export function faultText(since: string | null | undefined): string {
  return since
    ? `Kvar prijavljen od ${new Date(since).toLocaleString('sr-Latn-RS')}`
    : 'Kvar prijavljen'
}

function faultTint(critical: boolean) {
  return {
    fill: critical ? 'var(--color-brand-critical)' : 'var(--color-brand-warning)',
    bang: critical ? '#fff' : 'var(--color-brand-midnight)',
    text: critical ? 'text-brand-critical-text' : 'text-brand-warning-text',
  }
}

export function FaultBadge({ since, critical = false, size = 16 }: {
  since?: string | null
  critical?: boolean
  size?: number
}) {
  const t = faultTint(critical)
  const label = faultText(since)
  return (
    <svg role="img" aria-label={label} width={size} height={size} viewBox="0 0 24 24" className="shrink-0">
      <title>{label}</title>
      <path d={TRIANGLE_PATH} fill={t.fill} stroke="#fff" strokeWidth={1.5} strokeLinejoin="round" />
      <path d={BANG_PATH} stroke={t.bang} strokeWidth={2.4} strokeLinecap="round" fill="none" />
      <circle cx="12" cy="17.4" r="1.4" fill={t.bang} />
    </svg>
  )
}

// Icon + text line for cards and popups.
export function FaultNotice({ since, critical = false }: { since?: string | null; critical?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${faultTint(critical).text}`}>
      <FaultBadge since={since} critical={critical} size={14} />
      {faultText(since)}
    </span>
  )
}

// Same glyph as an HTML string, for Leaflet divIcons (markers live outside
// React's tree). Positioned top-right of the marker dot by the caller.
export function faultMarkerBadgeHtml({ since, critical, size }: { since?: string | null; critical: boolean; size: number }): string {
  const t = faultTint(critical)
  const label = faultText(since).replace(/"/g, '&quot;')
  return `<svg role="img" aria-label="${label}" width="${size}" height="${size}" viewBox="0 0 24 24" style="position:absolute;top:${-Math.round(size * 0.45)}px;right:${-Math.round(size * 0.55)}px;filter:drop-shadow(0 1px 1px rgba(0,0,0,.35));"><path d="${TRIANGLE_PATH}" fill="${t.fill}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/><path d="${BANG_PATH}" stroke="${t.bang}" stroke-width="2.4" stroke-linecap="round" fill="none"/><circle cx="12" cy="17.4" r="1.4" fill="${t.bang}"/></svg>`
}
